import { inject, provide, ssrRef, toRef, useContext } from '@nuxtjs/composition-api';
import { cookieKey } from '~/modules/common/utils/cookieUtils';

export const authStoreKey = Symbol('authStore');

export function installAuthContext() {
  /**
   * Auth State
   */
  const state = ssrRef(() => ({
    user: null,
    isLoggedIn: false,
    loginSource: null,
  }));

  const loginSources = {
    email: 'email',
    fb: 'fb',
  };

  const {
    res,
    $service,
    app: { $cookies },
  } = useContext();

  /**
   * Get auth token for a user
   */
  const getAuthToken = async ({ email, password }) => {
    const result = await $service.auth.getAuthToken({ email, password });
    setAuthTokenCookie(result.data.authToken);
    setRefreshTokenCookie(result.data.refreshToken);
    return result.data.authToken;
  };

  /**
   * Get auth token using google login
   */
  const getAuthTokenGoogle = async (accessTokenFromGoogle) => {
    const result = await $service.auth.getAuthTokenGoogle(accessTokenFromGoogle);
    setAuthTokenCookie(result.data.authToken);
    setRefreshTokenCookie(result.data.refreshToken);
    return result.data.authToken;
  };

  /**
   * Get auth token using facebook login
   */
  const getAuthTokenFB = async (accessTokenFromFacebook) => {
    const result = await $service.auth.getAuthTokenFB(accessTokenFromFacebook);
    setAuthTokenCookie(result.data.authToken);
    setRefreshTokenCookie(result.data.refreshToken);
    return result.data.authToken;
  };

  /**
   * Get request token
   */
  const getRequestTokenTwitter = async () => {
    const response = await $service.auth.getRequestTokenTwitter();
    state.value.twitterOAuthToken = response.data.oauthToken;
    $cookies.set('twitter-oauth-token-secret', response.data.oauthTokenSecret, {
      path: '/',
      maxAge: 60 * 60 * 24 * 30, // 30 gün
    });
  };

  /**
   * Get auth token using twitter login
   *
   * @param {string} oauthToken response token from twitter response
   * @param {string} oauthVerifier access token from twitter response
   */
  const getAuthTokenTwitter = async (oauthToken, oauthVerifier) => {
    const oauthTokenSecret = $cookies.get('twitter-oauth-token-secret');
    const result = await $service.auth.getAuthTokenTwitter(oauthToken, oauthTokenSecret, oauthVerifier);
    setAuthTokenCookie(result.data.authToken);
    setRefreshTokenCookie(result.data.refreshToken);
    return result.data.authToken;
  };

  /**
   * Get current user using AUTH_TOKEN cookie
   */
  const getCurrentUser = async () => {
    try {
      const { data } = await $service.auth.getCurrentUser();
      setLoginUser(data);
      res.setHeader('Cache-Control', 'private');
      return data;
    } catch (error) {}
  };

  /**
   * Init Authentication
   * called on every request
   *
   * @see @/plugins/globalSetup.js
   */
  const initAuth = async () => {
    // Check if user has login session from auth token at cookies
    const authToken = $cookies.get(cookieKey.AUTH_TOKEN);
    const refreshToken = $cookies.get(cookieKey.REFRESH_TOKEN);
    if (authToken || refreshToken) {
      await getCurrentUser();
    } else {
      const excludedCookies = ['twitter-oauth-token-secret', 'g_state', 'mdznads-custom-interstitial'];
      const cookiesList = Object.keys($cookies.getAll());
      cookiesList.map((cookie) => {
        if (excludedCookies.includes(cookie)) return;
        $cookies.remove(cookie);
      });
    }
  };

  /**
   * Login with email
   *
   * @param {object} params params
   * @param {string} params.email email
   * @param {string} params.password password
   */
  const loginWithEmail = async ({ email, password }) => {
    await getAuthToken({ email, password });
    await getCurrentUser();
  };

  /**
   * Login with twitter
   *
   * @param {string} oauthToken response token from twitter response
   * @param {string} oauthVerifier access token from twitter response
   */
  const loginWithTwitter = async (oauthToken, oauthVerifier) => {
    await getAuthTokenTwitter(oauthToken, oauthVerifier);
    await getCurrentUser();
  };

  /**
   * Login with facebook
   *
   * @param {string} accessTokenFromFacebook access token from facebook response
   */
  const loginWithFacebook = async (accessTokenFromFacebook) => {
    await getAuthTokenFB(accessTokenFromFacebook);
    await getCurrentUser();
  };

  /**
   * Login with Google
   *
   * @param {string} accessTokenFromGoogle access token from google response
   */
  const loginWithGoogle = async (accessTokenFromGoogle) => {
    await getAuthTokenGoogle(accessTokenFromGoogle);
    await getCurrentUser();
  };

  /**
   * Register user
   *
   * @param {string} user.email
   * @param {string} user.password
   */
  const register = async ({ email, password }) => {
    const result = await $service.auth.register({ email, password });
    setAuthTokenCookie(result.data.authToken);
    setRefreshTokenCookie(result.data.refreshToken);
    await getCurrentUser();
    return result.data.authToken;
  };

  /**
   * Check Username
   *
   * @param {string} username
   */
  const checkUsername = async (username) => {
    const result = await $service.auth.checkUsername(username);
    return result.data;
  };

  /**
   * Setup function for logged in user releated stuff
   * state, cookies, cache etc.
   *
   * @param {*} userData - user response
   * @param {*} loginType - login type
   */
  const setLoginUser = (userData, loginType = 'email') => {
    state.value.user = userData;
    state.value.loginSource = loginSources[loginType];
    state.value.isLoggedIn = true;
  };

  /**
   * set user avatar
   * @param {*} avatar - avatar object
   */
  const setUserAvatar = (avatar) => {
    state.value.user.avatar = avatar;
  };

  /**
   * set user username
   * @param {*} username - username
   */
  const setUsername = (username) => {
    state.value.user.username = username;
  };

  /**
   * Sets AUTH_TOKEN cookie for 30 days
   *
   * @param {string} accessToken access token
   */
  const setAuthTokenCookie = (accessToken) => {
    $cookies.set(cookieKey.AUTH_TOKEN, accessToken, {
      path: '/',
      maxAge: 60 * 60 * 24 * 30, // 30 gün
    });
  };

  /**
   * Sets REFRESH_TOKEN cookie for 60 days
   *
   * @param {string} refreshToken refresh token
   */
  const setRefreshTokenCookie = (refreshToken) => {
    $cookies.set(cookieKey.REFRESH_TOKEN, refreshToken, {
      path: '/',
      maxAge: 60 * 60 * 24 * 60, // 60 gün
    });
  };

  /**
   * Logout User and remove cookies
   */
  const logout = (deleteUserState = true) => {
    if (deleteUserState) {
      state.value.user = null;
      state.value.loginSource = null;
      state.value.isLoggedIn = false;
    }
    // clear cookies
    $cookies.removeAll();
    location.reload();
  };

  /**
   * Forgot password
   *
   * @param {object} params params object
   * @param {string} params.id username or email
   */
  const forgotPasswordMail = async ({ id }) => {
    const result = await $service.auth.forgotPasswordMail({ id });
    return result;
  };

  /**
   * Reset password
   *
   * @param {object} params params object
   * @param {string} params.token token
   * @param {string} params.password password
   */
  const resetPassword = async ({ token, password }) => {
    const { result } = await $service.auth.resetPassword({ token, password });
    return { result };
  };

  /**
   * Send Delete account mail to user
   *
   * @param {string} feedback reason
   */
  const deleteAccountMail = async (feedback) => {
    const { result } = await $service.auth.deleteAccountMail(feedback);
    return { result };
  };

  /**
   * Delete account
   *
   * @param {string} token token
   */
  const deleteAccount = async (token) => {
    const { result } = await $service.auth.deleteAccount(token);
    return { result };
  };

  /**
   * Deactivate account
   *
   * @param {string} feedback reason
   */
  const deactivateAccount = async (feedback) => {
    const { result } = await $service.auth.deactivateAccount(feedback);
    return { result };
  };

  // ...toRefs(state.value) de yapılabilir ama import ederken intellisense desteği olmadığı için tercih etmedik.
  provide(authStoreKey, {
    auth: state,
    user: toRef(state.value, 'user'),
    isLoggedIn: toRef(state.value, 'isLoggedIn'),
    loginSource: toRef(state.value, 'loginSource'),
    initAuth,
    setUserAvatar,
    setUsername,
    loginWithEmail,
    loginWithFacebook,
    loginWithGoogle,
    loginWithTwitter,
    getRequestTokenTwitter,
    logout,
    register,
    checkUsername,
    forgotPasswordMail,
    resetPassword,
    deleteAccountMail,
    deleteAccount,
    deactivateAccount,
    getAuthToken,
    getCurrentUser,
  });
}

/**
 * @typedef {Object} AuthState
 * @property {import("@nuxtjs/composition-api").Ref.<User>} user - current user
 * @property {import("@nuxtjs/composition-api").Ref.<boolean>} isLoggedIn - user is logged in
 * @property {import("@nuxtjs/composition-api").Ref.<string>} loginSource - user login source
 * @property {function} initAuth - initialize auth
 * @property {function} setUserAvatar - set user avatar
 * @property {function} setUsername - set user username
 * @property {function} loginWithEmail - Login with email and password
 * @property {function} loginWithFacebook - Login with facebook
 * @property {function} loginWithGoogle - Login with google
 * @property {function} loginWithTwitter - Login with twitter
 * @property {function} getRequestTokenTwitter - Get request token twitter
 * @property {function} getAuthToken - Get auth token
 * @property {function} getCurrentUser - Get current user
 * @property {function} register - register
 * @property {function} forgotPasswordMail - Forgot password mail
 * @property {function} resetPassword - Reset password
 * @property {function} deleteAccountMail - Delete account mail
 * @property {function} deleteAccount - Delete account
 * @property {function} deactivateAccount - Deactivate account
 * @property {function} logout - logout
 */

/**
 * @returns {AuthState} auth state
 */
export function useAuth() {
  return inject(authStoreKey);
}
